import { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../../../utils/Auth';
import { ColorAndGradientToggles, ColorPickers, Rotation, dotsTypes } from '../helper';
import styles from './Dots.module.css';

const getInitialColor = (options, isSecondColor = false) => {
    const colorStops = options?.gradient?.colorStops;
    if (isSecondColor) {
        return colorStops?.[1]?.color || '#000000';
    }
    return options?.color || colorStops?.[0]?.color || '#000000';
};

// Main Component
export default function Dots() {
    const { qr_options, optionsDispatch } = useAuth();
    const dotsOptions = qr_options?.dotsOptions;

    const colorType = useMemo(() => dotsOptions?.gradient ? 'gradient' : 'single', [dotsOptions?.gradient]);
    const gradientType = useMemo(() => dotsOptions?.gradient?.type || 'linear', [dotsOptions?.gradient?.type]);
    const firstColor = useMemo(() => getInitialColor(dotsOptions), [dotsOptions]);
    const secondColor = useMemo(() => getInitialColor(dotsOptions, true), [dotsOptions]);
    const rotation = useMemo(() => dotsOptions?.gradient?.rotation || '0', [dotsOptions?.gradient?.rotation]);

    const [ displayColorPicker, setDisplayColorPicker ] = useState(null);

    const handleDotsTypeChange = (newType) => {
        optionsDispatch({ type: 'dots', options: { type: newType }})
    };

    const handleColorTypeChange = (e) => {
        const newColorType = e.target.checked ? 'gradient' : 'single';

        if (newColorType === 'single') {
            optionsDispatch({ type: 'dots', options: { color: firstColor, gradient: null }});
        } else {
            optionsDispatch({ type: 'dots', options: { color: null, gradient: { colorStops: [{ offset: 0, color: firstColor }, { offset: 1, color: secondColor }], type: gradientType }}});
        };
    };

    const handleGradientTypeChange = (e) => {
        const newGradientType = e.target.checked ? 'radial' : 'linear';
        optionsDispatch({ type: 'dots', options: { gradient: { ...dotsOptions.gradient, type: newGradientType }}});
    };

    const handleColorChange = useCallback((color, name) => {
        if (colorType === 'single') {
            optionsDispatch({ type: 'dots', options: { color: color }});
        } else {
            if (name === 'single-color') {
                optionsDispatch({ type: 'dots', options: { gradient: { ...dotsOptions.gradient, colorStops: [{ offset: 0, color: color }, { offset: 1, color: secondColor }]}}});
            } else if (name === 'gradient-color') {
                optionsDispatch({ type: 'dots', options: { gradient: { ...dotsOptions.gradient, colorStops: [{ offset: 0, color: firstColor }, { offset: 1, color: color }]}}});
            };
        };
    },[colorType, firstColor, secondColor, dotsOptions, optionsDispatch]);

    const handleRotationChange = (e) => {
        const rotation = e.target.value;
        optionsDispatch({ type: 'dots', options: { gradient: { ...dotsOptions.gradient, rotation: rotation }}});
    };

    return (
        <div className={styles.container}>
            <div className={styles.inputGroup} style={{marginBottom: '0'}}>
                <div className={styles.label}>Dots Style</div>
                <div className={styles.dotsWrap}>
                    {dotsTypes.map(type => (
                        <div key={type.value} className={dotsOptions?.type === type.value ? `${styles.dots} ${styles.selected}` : styles.dots} onClick={() => handleDotsTypeChange(type.value)}>
                            <img src={type.imgSrc} alt='type of dots' />
                            <p>{type.name}</p>
                        </div>
                    ))}
                </div>
            </div>
            <div className={styles.inputGroup} style={{flexDirection: 'row', gap: '0 1.25rem'}}>
                <ColorAndGradientToggles
                    colorType={colorType}
                    handleColorTypeChange={handleColorTypeChange}
                    gradientType={gradientType}
                    handleGradientTypeChange={handleGradientTypeChange}
                />
                <Rotation 
                    show={colorType === 'gradient' && gradientType === 'linear'} 
                    rotation={rotation}
                    handleRotationChange={handleRotationChange}
                />
            </div>
            <div className={styles.inputGroup} style={{marginBottom: '0'}}>
                <div className={styles.label}>Dots {colorType === 'single' ? 'Color' : 'Gradient'}</div>
                <ColorPickers 
                    colorType={colorType}
                    firstColor={firstColor}
                    secondColor={secondColor}
                    handleColorChange={handleColorChange}
                    displayColorPicker={displayColorPicker}
                    setDisplayColorPicker={setDisplayColorPicker}
                />
            </div>
        </div>
    );
};